import moment from "moment";
// `formatPeriodLabel` devuelve etiquetas en español ("ene 2026", "Q1 2025",
// "2025"). Cargar el bundle de locale acá garantiza que cualquier consumidor
// del helper (incluidos los tests del store, que no cargan VentasTab.vue) vea
// las mismas etiquetas que ven los usuarios. El locale es side-effect global
// pero idempotente — importar el módulo dos veces no rompe nada.
import "moment/locale/es";

// Utilidades de rango de fechas compartidas entre los stores de Resultados,
// Ventas y Gastos. Mantener las dos funciones aquí evita que cada store
// duplique la misma lógica de switch + buildMonthHeaders.

// Cantidad de columnas de año que muestran por default las granularidades
// multi-año (YTD y Anual) cuando no hay un rango custom. Es el CONTEO exacto de
// años visibles — 3 → año actual + los 2 previos. Constante única para ambas
// granularidades (decisión de UX compartida por todos los reportes); centralizar
// el número acá evita que se desincronice entre tabs.
export const DEFAULT_ANNUAL_YEARS = 3;

// Rango de años por default: las últimas DEFAULT_ANNUAL_YEARS columnas hasta el
// año de `endMonth`. Fuente única consumida por el fetch (`_resolveDateRange`,
// para YTD y Anual) y por el trigger del picker de Anual (computed `yearRange`
// de los tabs), para que el default mostrado y el consultado nunca difieran.
// Endpoints normalizados a Ene 1, igual que los que emite el AnualCalendar.
export function defaultYearRange(endMonth) {
    const endYear = moment(endMonth).year();
    return {
        from: new Date(endYear - (DEFAULT_ANNUAL_YEARS - 1), 0, 1),
        to: new Date(endYear, 0, 1),
    };
}

// `selectedPeriodRange = "custom"` indica que el usuario eligió un rango
// explícito en el calendar (modo range — hoy sólo Ventas). El `customRange`
// extra es `{ from: Date, to: Date }` y reemplaza a `endMonth` + range
// predefinido. Cuando viene incompleto, caemos al fallback de "lastYear" sobre
// `endMonth` para no devolver un rango inválido.
//
// El rango custom SÓLO se aplica con granularidad mensual o trimestral. Para
// YTD/Anual el contrato cross-tab es "las últimas DEFAULT_ANNUAL_YEARS columnas
// de año" — un rango custom de meses no tiene mapping coherente a años
// completos, así que cuando el filterPeriod cambia a ytd/year ignoramos el
// customRange (sigue en state, no se borra: cuando el usuario vuelve a Mensual
// o Trimestral recupera lo que había seleccionado).
// `dataFirstMonth` (opcional): primer mes con data de la empresa
// (reportsFilters.dataFirstMonth, fuente backend get_first_report_month). Si
// viene, el inicio del rango se recorta a ese mes — los períodos viejos sin
// data no se muestran. El recorte es SOLO hacia el pasado: endDate nunca se
// toca, y si el primer dato es posterior al fin del rango pedido el rango
// queda intacto (el tab muestra su estado "sin datos").
export function resolveDateRange(endMonth, selectedPeriodRange, filterPeriod = null, customRange = null, customYearRange = null, dataFirstMonth = null) {
    const range = _resolveRawRange(endMonth, selectedPeriodRange, filterPeriod, customRange, customYearRange);
    return _clampInitToFirstDataMonth(range, dataFirstMonth);
}

function _clampInitToFirstDataMonth(range, dataFirstMonth) {
    if (!dataFirstMonth) {
        return range;
    }
    const firstMonth = moment(dataFirstMonth).startOf("month");
    if (!firstMonth.isValid()) {
        return range;
    }
    const initDate = moment(range.initDate);
    const endDate = moment(range.endDate);
    if (firstMonth.isAfter(initDate) && firstMonth.isSameOrBefore(endDate)) {
        return { ...range, initDate: firstMonth.format("YYYY-MM-DD") };
    }
    return range;
}

function _resolveRawRange(endMonth, selectedPeriodRange, filterPeriod = null, customRange = null, customYearRange = null) {
    const customRangeApplies = (filterPeriod === null || filterPeriod === "month" || filterPeriod === "quarter")
        && selectedPeriodRange === "custom"
        && customRange
        && customRange.from
        && customRange.to;
    if (customRangeApplies) {
        return {
            initDate: moment(customRange.from).startOf("month").format("YYYY-MM-DD"),
            endDate: moment(customRange.to).endOf("month").format("YYYY-MM-DD"),
        };
    }
    // Rango de años explícito (granularidad "year"). Independiente del
    // `customRange` de meses: el usuario elige un span de años calendario que
    // reemplaza el default de DEFAULT_ANNUAL_YEARS columnas. `from`/`to` son
    // Dates normalizadas a Ene 1 del año por el AnualCalendar; expandimos al
    // año completo (Ene 1 → Dic 31). Sin selección válida, cae al fallback.
    const customYearRangeApplies = filterPeriod === "year"
        && customYearRange
        && customYearRange.from
        && customYearRange.to;
    if (customYearRangeApplies) {
        return {
            initDate: moment(customYearRange.from).startOf("year").format("YYYY-MM-DD"),
            endDate: moment(customYearRange.to).endOf("year").format("YYYY-MM-DD"),
        };
    }
    // YTD con rango de años explícito: el usuario elige el span de años (from→to)
    // y el mes de corte (el mes de `endMonth`). init = Ene 1 del año `from`; end =
    // fin del mes de corte EN el año `to` (no en el año de endMonth — así el span
    // lo fija el rango, no la fecha). El backend recorta cada año a ese mes
    // (`ytd_month_limit = end_date.month`), dejando las columnas comparables.
    const customYtdApplies = filterPeriod === "ytd"
        && customYearRange
        && customYearRange.from
        && customYearRange.to;
    if (customYtdApplies) {
        const toYear = moment(customYearRange.to).year();
        const cutoffMonth = moment(endMonth).month(); // 0-11
        return {
            initDate: moment(customYearRange.from).startOf("year").format("YYYY-MM-DD"),
            endDate: moment({ year: toYear, month: cutoffMonth, day: 1 })
                .endOf("month")
                .format("YYYY-MM-DD"),
        };
    }
    return _resolveDateRange(endMonth, selectedPeriodRange, filterPeriod);
}

function _resolveDateRange(endMonth, selectedPeriodRange, filterPeriod = null) {
    const refEnd = moment(endMonth).endOf("month");
    let initDate;
    let endDate = refEnd.format("YYYY-MM-DD");

    // Para "lastYear"/"last24months" usamos subtract(N-1) en lugar de N: el
    // rango [initDate..endDate] es inclusivo de ambos extremos, así que para
    // ver exactamente N meses (endMonth incluido) hay que retroceder N-1.
    // subtract(12) producía 13 cabeceras (1 + 12 meses anteriores), que no
    // matchea la semántica del label "Último año".
    switch (selectedPeriodRange) {
        case "lastYear":
            initDate = moment(refEnd).subtract(11, "months").startOf("month").format("YYYY-MM-DD");
            break;
        case "last24months":
            initDate = moment(refEnd).subtract(23, "months").startOf("month").format("YYYY-MM-DD");
            break;
        case "actualYear":
            initDate = moment(refEnd).startOf("year").format("YYYY-MM-DD");
            break;
        case "previousYear": {
            // Año calendario anterior al de refEnd — self-contained: el rango
            // se computa sin depender de que `setPeriodRange` haya pre-shifteado
            // endMonth a Dec del año previo. Antes la fórmula era idéntica a
            // `actualYear` y solo funcionaba por la coincidencia de que
            // RANGE_OVERRIDES movía endMonth; cuando el usuario cambiaba la
            // fecha por el calendario después de elegir "Año Anterior",
            // la query terminaba pidiendo Jan→endMonth del año en curso.
            const prevYearRef = moment(refEnd).subtract(1, "year");
            initDate = prevYearRef.clone().startOf("year").format("YYYY-MM-DD");
            endDate = prevYearRef.clone().endOf("year").format("YYYY-MM-DD");
            break;
        }
        default:
            initDate = moment(refEnd).subtract(11, "months").startOf("month").format("YYYY-MM-DD");
    }

    // Override de granularidad multi-año: cuando el filtro es year o ytd, los
    // stores quieren mostrar varios años calendario hacia atrás como columnas —
    // ignorando el `selectedPeriodRange`. Antes este bloque vivía duplicado en
    // cada uno de los 6 stores de reportes; lo movimos acá para que el contrato
    // sea único. YTD y Anual comparten el default de DEFAULT_ANNUAL_YEARS
    // columnas (vía `defaultYearRange`, misma fuente que el trigger del picker).
    if (filterPeriod === "year" || filterPeriod === "ytd") {
        initDate = moment(defaultYearRange(endMonth).from)
            .startOf("year")
            .format("YYYY-MM-DD");
    }

    return { initDate, endDate };
}

export function buildMonthHeaders(initDate, endDate) {
    const headers = [];
    let cursor = moment(initDate).startOf("month");
    const end = moment(endDate).startOf("month");

    while (cursor.isSameOrBefore(end)) {
        headers.push(cursor.format("MMM YYYY").replace(".", ""));
        cursor.add(1, "months");
    }
    return headers;
}

// Formato de etiqueta de período según granularidad solicitada.
// month → "Ene 2025", quarter → "Q1 2025", year/ytd → "2025".
// El `.locale("es")` explícito evita depender del default global de moment
// — distintos puntos del app (bankList, BankReconciliation, etc.) lo cambian
// como side-effect en mounted, así que sin esta llamada los tests del store
// verían "Jan" en lugar de "ene" cuando esos componentes no están en el árbol.
//
// Capitalizamos la primera letra del bucket month — antes el moment "es"
// devolvía "ene 2025" y la tabla lo subía a "Ene 2025" via CSS
// `text-transform: capitalize`. Eso dejaba los consumers que no aplican CSS
// (axisLabel de ECharts en los charts del header) con el formato lowercase y
// el resto de la app con el capitalizado. Mover la capitalización al source
// hace el formato sistémico: el dato es uno y se renderea igual en todos
// lados. El re-parseo del label (`periodLastMonth`) no se rompe — moment.js es
// case-insensitive parseando month abbreviations en un locale.
export function formatPeriodLabel(period, filterPeriod) {
    const m = moment(period).locale("es");
    switch (filterPeriod) {
        case "quarter": return m.format("[Q]Q YYYY").replace(".", "");
        case "year":
        case "ytd": return m.format("YYYY").replace(".", "");
        case "month":
        default: {
            const label = m.format("MMM YYYY").replace(".", "");
            return label.charAt(0).toUpperCase() + label.slice(1);
        }
    }
}

// Cabeceras únicas dentro del rango, respetando la granularidad solicitada.
// Recorre mes a mes y deduplica etiquetas (Q1 2025 aparece una sola vez aunque
// el rango cubra ene/feb/mar).
export function buildHeadersByPeriod(initDate, endDate, filterPeriod) {
    const headers = [];
    let cursor = moment(initDate).startOf("month");
    const end = moment(endDate).startOf("month");

    while (cursor.isSameOrBefore(end)) {
        const label = formatPeriodLabel(cursor, filterPeriod);
        if (!headers.includes(label)) {
            headers.push(label);
        }
        cursor.add(1, "months");
    }
    return headers;
}
